"use client";
import {
  Accordion,
  AccordionHeading,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Button,
  Switch,
} from "@heroui/react";
import { Icon } from "@/components/ui/icon";
import { useEffect, useState } from "react";
import { useConsent } from './use-consent';

interface PrivacySettingsState {
  cookies: {
    analytics: boolean;
    advertising: boolean;
    functional: boolean;
  };
  integrations: {
    microsoft: {
      dataAccess: boolean;
      dataSharing: boolean;
      emailAccess: boolean;
      calendarAccess: boolean;
      fileAccess: boolean;
    };
    google: {
      dataAccess: boolean;
      dataSharing: boolean;
      emailAccess: boolean;
      calendarAccess: boolean;
      fileAccess: boolean;
    };
    facebook: {
      dataAccess: boolean;
      dataSharing: boolean;
      emailAccess: boolean;
      calendarAccess: boolean;
      fileAccess: boolean;
    };
  };
}

interface PrivacySettingsProps {
  onClose?: () => void;
}

export default function PrivacySettings({ onClose }: PrivacySettingsProps) {
  const { setMaxPrivacy, setMaxConvenience, setCustomConsent, privacyState } = useConsent();

  const [settings, setSettings] = useState<PrivacySettingsState>({
    cookies: {
      analytics: privacyState.cookieConsent.analytics,
      advertising: privacyState.cookieConsent.advertising,
      functional: privacyState.cookieConsent.functional,
    },
    integrations: {
      microsoft: { ...privacyState.microsoftIntegration },
      google: { ...privacyState.googleIntegration },
      facebook: { ...privacyState.facebookIntegration },
    },
  });

  // Sync state with privacyState when it changes
  useEffect(() => {
    setSettings({
      cookies: {
        analytics: privacyState.cookieConsent.analytics,
        advertising: privacyState.cookieConsent.advertising,
        functional: privacyState.cookieConsent.functional,
      },
      integrations: {
        microsoft: { ...privacyState.microsoftIntegration },
        google: { ...privacyState.googleIntegration },
        facebook: { ...privacyState.facebookIntegration },
      },
    });
  }, [privacyState]);

  const handleMaxPrivacy = () => {
    setMaxPrivacy();

    // Update local state to reflect changes
    setSettings({
      cookies: {
        analytics: false,
        advertising: false,
        functional: false,
      },
      integrations: {
        microsoft: {
          dataAccess: false,
          dataSharing: false,
          emailAccess: false,
          calendarAccess: false,
          fileAccess: false,
        },
        google: {
          dataAccess: false,
          dataSharing: false,
          emailAccess: false,
          calendarAccess: false,
          fileAccess: false,
        },
        facebook: {
          dataAccess: false,
          dataSharing: false,
          emailAccess: false,
          calendarAccess: false,
          fileAccess: false,
        },
      },
    });
  };

  const handleMaxConvenience = () => {
    setMaxConvenience();

    // Update local state to reflect changes
    setSettings({
      cookies: {
        analytics: true,
        advertising: true,
        functional: true,
      },
      integrations: {
        microsoft: {
          dataAccess: true,
          dataSharing: true,
          emailAccess: true,
          calendarAccess: true,
          fileAccess: true,
        },
        google: {
          dataAccess: true,
          dataSharing: true,
          emailAccess: true,
          calendarAccess: true,
          fileAccess: true,
        },
        facebook: {
          dataAccess: true,
          dataSharing: true,
          emailAccess: true,
          calendarAccess: true,
          fileAccess: true,
        },
      },
    });
  };

  const handleSavePreferences = () => {
    setCustomConsent(
      {
        ...settings.cookies,
        necessary: true,
      },
      {
        microsoft: settings.integrations.microsoft,
        google: settings.integrations.google,
        facebook: settings.integrations.facebook,
      }
    );

    // Close modal if onClose is provided
    if (onClose) {
      onClose();
    }
  };

  const updateCookieSettings = (key: keyof typeof settings.cookies, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      cookies: {
        ...prev.cookies,
        [key]: value,
      }
    }));
  };

  const updateIntegrationSettings = (
    integration: keyof typeof settings.integrations,
    key: string,
    value: boolean
  ) => {
    setSettings(prev => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        [integration]: {
          ...prev.integrations[integration],
          [key]: value,
        }
      }
    }));
  };

  return (
    (<div className="max-w-4xl p-4 max-sm:p-2 mx-auto">
      <div className="mb-6 max-sm:mb-3">
        <h2 className="mb-4 max-sm:mb-2 text-2xl max-sm:text-lg font-bold">Privacy Settings</h2>
        <p className="mb-4 max-sm:mb-2 max-sm:text-sm">
          Choose your privacy preferences. You can customize individual settings or choose a preset option.
        </p>

        <div className="flex flex-wrap gap-2 mb-6 max-sm:mb-3">
          <Button
            variant="danger"
            size="sm"
            onPress={handleMaxPrivacy}
          >
            Max Privacy
          </Button>
          <Button
            size="sm"
            onPress={handleMaxConvenience}
          >
            Max Convenience
          </Button>
          <Button
            onPress={handleSavePreferences}
            variant="secondary"
            size="sm"
          >
            Save Preferences
            <Icon
              icon="lock"
              className="inline-block ml-1"
              width={16}
              height={16}
            />
          </Button>
        </div>

        <Accordion>
          <AccordionItem
            key="cookies"
            id="cookies"
          >
            <AccordionHeading>
              <AccordionTrigger className='w-full flex flex-col items-start'>
                <span>Cookie Preferences</span>
                <span className='text-sm font-normal opacity-70'>Control how we use cookies to improve your experience</span>
              </AccordionTrigger>
            </AccordionHeading>
            <AccordionPanel>
            <div className="flex flex-col gap-4 px-2">
              <Switch
                isSelected={settings.cookies.analytics}
                onChange={(value) => updateCookieSettings('analytics', value)}
              >
                <Switch.Content>
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                  Analytics Cookies
                </Switch.Content>
              </Switch>
              <Switch
                isSelected={settings.cookies.advertising}
                onChange={(value) => updateCookieSettings('advertising', value)}
              >
                <Switch.Content>
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                  Advertising Cookies
                </Switch.Content>
              </Switch>
              <Switch
                isSelected={settings.cookies.functional}
                onChange={(value) => updateCookieSettings('functional', value)}
              >
                <Switch.Content>
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                  Functional Cookies
                </Switch.Content>
              </Switch>
            </div>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem
            key="integrations"
            id="integrations"
          >
            <AccordionHeading>
              <AccordionTrigger className='w-full flex flex-col items-start'>
                <span>Integration Permissions</span>
                <span className='text-sm font-normal opacity-70'>Manage third-party service integrations</span>
              </AccordionTrigger>
            </AccordionHeading>
            <AccordionPanel>
            {Object.entries(settings.integrations).map(([provider, permissions]) => (
              <div key={provider} className="mb-6">
                <h4 className="mb-2 font-medium capitalize text-medium">{provider}</h4>
                <div className="flex flex-col gap-2 px-2">
                  {Object.entries(permissions).map(([key, value]) => (
                    <Switch
                      key={`${provider}-${key}`}
                      size="sm"
                      isSelected={value}
                      onChange={(checked) =>
                        updateIntegrationSettings(
                          provider as keyof typeof settings.integrations,
                          key,
                          checked
                        )
                      }
                    >
                      <Switch.Content>
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </Switch.Content>
                    </Switch>
                  ))}
                </div>
              </div>
            ))}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </div>
    </div>)
  );
}
